import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { config } from '@/config';

const { accessTokenKey, refreshTokenKey } = config.storage;

export const tokenStore = {
  getAccess: () => localStorage.getItem(accessTokenKey),
  setAccess: (t: string) => localStorage.setItem(accessTokenKey, t),
  getRefresh: () => localStorage.getItem(refreshTokenKey),
  setRefresh: (t: string) => localStorage.setItem(refreshTokenKey, t),
  clear: () => {
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);
  },
};

export const publicClient = axios.create({
  baseURL: config.api.base,
  headers: { 'Content-Type': 'application/json' },
});

export const authClient = axios.create({
  baseURL: config.api.base,
  headers: { 'Content-Type': 'application/json' },
});

authClient.interceptors.request.use((cfg) => {
  const t = tokenStore.getAccess();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // FormData uploads need the browser to set `Content-Type:
  // multipart/form-data; boundary=...` itself. Our axios defaults
  // include `Content-Type: application/json` (line 24), and axios
  // does NOT auto-override that when a FormData body is supplied —
  // it instead JSON-stringifies the FormData (which yields "{}" since
  // FormData has no enumerable own props) and sends an empty body.
  // That was the 2026-06-04 face-enrollment 422 ("body={}, name
  // missing"). Clearing the header here lets the browser negotiate
  // the correct multipart Content-Type with the random boundary.
  if (typeof FormData !== 'undefined' && cfg.data instanceof FormData) {
    if (cfg.headers && 'Content-Type' in cfg.headers) {
      delete (cfg.headers as Record<string, unknown>)['Content-Type'];
    }
  }
  return cfg;
});

let refreshing = false;
let queue: Array<(token: string | null) => void> = [];

const flush = (t: string | null) => {
  queue.forEach((cb) => cb(t));
  queue = [];
};

authClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (refreshing) {
      return new Promise((resolve, reject) => {
        queue.push((t) => {
          if (!t) return reject(error);
          original.headers = { ...original.headers, Authorization: `Bearer ${t}` };
          resolve(authClient(original));
        });
      });
    }

    original._retry = true;
    refreshing = true;
    const refreshToken = tokenStore.getRefresh();

    if (!refreshToken) {
      tokenStore.clear();
      flush(null);
      refreshing = false;
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return Promise.reject(error);
    }

    try {
      const { data } = await publicClient.post(config.api.auth.refresh, {
        refresh_token: refreshToken,
      });
      const tokens = data?.tokens ?? data;
      const newAccess = tokens.access_token as string;
      tokenStore.setAccess(newAccess);
      if (tokens.refresh_token) tokenStore.setRefresh(tokens.refresh_token);
      flush(newAccess);
      original.headers = { ...original.headers, Authorization: `Bearer ${newAccess}` };
      return authClient(original);
    } catch (err) {
      tokenStore.clear();
      flush(null);
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return Promise.reject(err);
    } finally {
      refreshing = false;
    }
  },
);
